import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { SearchHitCarousel } from '#/components/SearchHitCarousel/SearchHitCarousel';
import { UnpublishedListing } from '#/components/UnpublishedListing/UnpublishedListing';
import { useAppConfig } from '#/config/AppConfigContext';
import { useFetchFn } from '#/hooks/useFetchFn';
import { useI18n } from '#/i18n/hooks/useI18n';
import { trackViewListing } from '#/modules/listing/analytics/trackViewListing';
import type { ApiUnpublishedEvent } from '#/modules/listing/events/api/types/ApiUnpublishedEvent';
import { searchAlgoliaFetchRecommendedListings } from '#/modules/search/algolia/fetch/searchAlgoliaFetchRecommendedListings';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { searchGetEventsRoutePath } from '#/modules/search/events/routing/searchGetEventsRoutePath';
import { getParsedSearchQuery } from '#/modules/search/helpers/getParsedSearchQuery';
import type { SearchPageLocationState } from '#/modules/search/pages/Search/types/SearchPageLocationState';

import { getUnpublishedEventPageSeeMoreText } from './getUnpublishedEventPageSeeMoreText';

type Props = {
  unpublishedEvent: ApiUnpublishedEvent;
};

export function UnpublishedEventPage({ unpublishedEvent }: Props) {
  const { appConfig } = useAppConfig();
  const { i18n, locale } = useI18n();

  const { listingName, id, org } = unpublishedEvent;

  useEffect(() => {
    trackViewListing({
      status: 'unpublished',
      hasAts: false,
      listingName,
      listingId: id,
      listingType: 'EVENT',
      orgId: org ? org.id : undefined,
      orgName: org ? org.name : undefined,
      orgType: org ? org.orgType : undefined,
    });
  }, [id, listingName, org]);

  const { searchContext } = useSearchContextHeader({
    currentObjectId: unpublishedEvent.id,
  });

  const [recommendedListingsResult] = useFetchFn({
    fn: searchAlgoliaFetchRecommendedListings,
    args: [unpublishedEvent, appConfig],
  });

  const recommendedListings = recommendedListingsResult?.hits ?? [];

  const navigate = useNavigate();
  const search = useCallback(
    (query: string) => {
      const { parsedQuery, locationType, remoteKeywords } =
        getParsedSearchQuery({ query, locale });

      void navigate(
        searchGetEventsRoutePath({
          locale,
          q: parsedQuery,
          filtersByName: { locationType },
        }),
        {
          state: {
            searchFrom: 'Unpublished Event Page',
            remoteKeywords,
          } satisfies SearchPageLocationState,
        },
      );
    },
    [locale, navigate],
  );

  const seeMoreText = getUnpublishedEventPageSeeMoreText({
    i18n,
    orgUrl: unpublishedEvent.org
      ? `${unpublishedEvent.org.url[locale]}#opportunities`
      : undefined,
    orgPublishedEventsCount: unpublishedEvent.orgPublishedEventsCount ?? 0,
  });

  return (
    <UnpublishedListing
      searchContext={searchContext}
      footerTitle={i18n('Search Events')}
      seeMoreText={seeMoreText}
      searchFormAnalyticsTitle="[main] View Event Page (Unpublished) - search"
      onSubmit={({ values }) => search(values.query)}
      exploreSection={
        recommendedListings.length > 0 && (
          <SearchHitCarousel
            headline={i18n('Explore Similar Events on Idealist')}
            results={recommendedListings}
            seeAll={{
              text: i18n('See all events'),
              action: { type: 'button', onClick: () => search('') },
            }}
            listingType="EVENT"
            carouselLocation="Event Listing"
            fromListingId={unpublishedEvent.id}
          />
        )
      }
    />
  );
}
